/**
 * Portal-based async typeahead used by the v2 Provisional Forecast
 * Landing page. Adapted directly from the BD Portal Wiki landing
 * pattern (`screens/BDPortal/ProductWiki/.../SearchSection/AsyncSearchInput.tsx`)
 * because users specifically liked the smoothness of that one.
 *
 * Key behaviours we keep from the wiki version:
 *
 *   - 500ms input debounce + AbortController-style cancellation via the
 *     `loadOptions` callback (the parent's `loadOptions` should cancel
 *     stale in-flight requests on its own — see store.runSearch).
 *   - "Searching…" placeholder while a query is in-flight OR while a
 *     debounce timeout is still pending (no flash of "No results").
 *   - "No results found" only when ALL of: search completed, not loading,
 *     no pending timer, and the input still matches the term we
 *     actually searched. Avoids flicker between keystrokes.
 *   - Skip the API call after a selection (`justSelectedRef`) so picking
 *     an item never re-fires the search for its own label.
 *   - createPortal()'d dropdown so it can't get clipped by overflow:
 *     hidden on parent containers.
 *   - Keyboard nav (Up/Down/Enter/Escape).
 *
 * Differences from the wiki version:
 *
 *   - Generic `<TOption>` — works with any option shape; the parent
 *     supplies an `itemTemplate` for custom rendering (we want a
 *     two-line "concept / GFC" row).
 *   - Optional `onChange` so the parent's input value can be MobX-
 *     backed without a useState dance.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface V2AsyncSearchInputProps<TOption> {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelect: (option: TOption) => void;
  onClear?: () => void;
  loadOptions: (query: string) => Promise<TOption[]>;
  /** Render each row in the dropdown.
   *
   * `isLastSelected` is true for the row the user most recently
   * clicked (only meaningful when `closeOnSelect` is false). The
   * default rendering also adds a left accent bar to that row so
   * the parent doesn't need to handle it explicitly, but the flag
   * is exposed for callers that want richer styling.
   */
  itemTemplate: (
    option: TOption,
    isHovered: boolean,
    isLastSelected: boolean
  ) => React.ReactNode;
  /** Map an option to its key (avoids index-based React keys). */
  getOptionKey: (option: TOption) => string;
  placeholder?: string;
  /** Minimum input length before firing a search. Defaults to 2. */
  minLength?: number;
  /** Debounce in ms. Defaults to 500. */
  debounceMs?: number;
  /**
   * If true (default): clicking a suggestion fires onSelect AND closes
   * the dropdown / clears local result state — the standard
   * "pick-one-thing" autocomplete behaviour.
   *
   * If false: clicking only fires onSelect. The input value stays
   * exactly what the user typed, the dropdown stays open with the
   * same results, and the clicked row keeps a visual highlight so
   * the user can see what they just opened. Useful when "selection"
   * means "open detail view about this", not "commit this value".
   */
  closeOnSelect?: boolean;
}

const V2AsyncSearchInput = <TOption,>({
  inputValue,
  onInputChange,
  onSelect,
  onClear,
  loadOptions,
  itemTemplate,
  getOptionKey,
  placeholder = 'Search…',
  minLength = 2,
  debounceMs = 500,
  closeOnSelect = true,
}: V2AsyncSearchInputProps<TOption>) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [options, setOptions] = useState<TOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  // Tracks which option the user last clicked when closeOnSelect=false,
  // so we can keep it visually highlighted even after the dropdown
  // re-renders or the user mouses elsewhere. Stored as the option's
  // key (via getOptionKey) so identity survives across loadOptions
  // re-runs that may return new object references.
  const [lastSelectedKey, setLastSelectedKey] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justSelectedRef = useRef(false);
  const lastSearchedTermRef = useRef('');

  // ── dropdown positioning (anchored to the input via portal) ─────────── //
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownPosition();
    const handle = () => updateDropdownPosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [showDropdown]);

  // ── debounced async load on input change ────────────────────────────── //
  useEffect(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    // Skip the API call right after an option pick (the parent will
    // typically push the option's label back into inputValue).
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (inputValue.length >= minLength) {
      setIsLoading(true);
      setHasSearched(false);
      setOptions([]);
      setShowDropdown(true);

      loadTimeoutRef.current = setTimeout(async () => {
        const searchTerm = inputValue;
        try {
          const results = await loadOptions(inputValue);
          setOptions(results);
          setHasSearched(true);
          setIsLoading(false);
          lastSearchedTermRef.current = searchTerm;
          loadTimeoutRef.current = null;
        } catch {
          setOptions([]);
          setHasSearched(true);
          setIsLoading(false);
          lastSearchedTermRef.current = searchTerm;
          loadTimeoutRef.current = null;
        }
      }, debounceMs);
    } else {
      setOptions([]);
      setShowDropdown(false);
      setIsLoading(false);
      setHasSearched(false);
      lastSearchedTermRef.current = '';
    }

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [inputValue, loadOptions, minLength, debounceMs]);

  // ── handlers ────────────────────────────────────────────────────────── //
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    justSelectedRef.current = false;
    onInputChange(e.target.value);
    setHoveredIndex(-1);
    // User is typing again — clear the "last opened" highlight, since
    // a fresh search is about to fire and the previous selection is
    // no longer in context.
    setLastSelectedKey(null);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (
      inputValue.length >= minLength &&
      (options.length > 0 || isLoading)
    ) {
      setShowDropdown(true);
    }
  };

  const handleOptionClick = (option: TOption) => {
    onSelect(option);

    if (closeOnSelect) {
      // Standard autocomplete behaviour: collapse and clear so the
      // parent can stuff the chosen value back into `inputValue`
      // without retriggering a search.
      justSelectedRef.current = true;
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      setShowDropdown(false);
      setIsFocused(false);
      setOptions([]);
      setIsLoading(false);
      setHasSearched(false);
      lastSearchedTermRef.current = '';
      setLastSelectedKey(null);
    } else {
      // "Open detail" behaviour: dropdown stays open, results stay
      // intact, just-clicked row remains visually highlighted so the
      // user can see what's now open. We do NOT set
      // justSelectedRef — there's nothing for the parent to push
      // back into `inputValue` since the input still reflects what
      // the user typed.
      setLastSelectedKey(getOptionKey(option));
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
    onInputChange('');
    setOptions([]);
    setShowDropdown(false);
    setHasSearched(false);
    setLastSelectedKey(null);
    lastSearchedTermRef.current = '';
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoveredIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoveredIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && hoveredIndex >= 0) {
      e.preventDefault();
      handleOptionClick(options[hoveredIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────── //
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          width: '100%',
          height: '5rem',
          padding: '1.5rem 5rem 1.5rem 2rem',
          fontSize: '1.25rem',
          border: 'none',
          borderRadius: 'var(--border-radius-lg, 12px)',
          boxShadow: isFocused
            ? '0 20px 40px rgba(0,0,0,0.18), 0 0 0 4px rgba(59, 130, 246, 0.12)'
            : '0 8px 24px rgba(0,0,0,0.08)',
          outline: 'none',
          background: isFocused ? 'white' : 'rgba(255, 255, 255, 0.96)',
          transition: 'all 0.4s ease',
          transform: isFocused ? 'translateY(-3px)' : 'none',
          fontFamily: 'inherit',
          color: '#000',
        }}
      />

      {inputValue && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '1.25rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#9CA3AF',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Clear search"
        >
          ×
        </button>
      )}

      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              backgroundColor: 'white',
              borderRadius: 'var(--border-radius, 8px)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.18)',
              maxHeight: 400,
              overflowY: 'auto',
              // Sit above page content but below overlay surfaces
              // (PrimeReact Sidebar/Dialog use 1101–1102). The
              // wiki version uses 999999 which clobbers everything
              // including the sidebar — see the reported bug where
              // the search dropdown overlapped the v2 sidebar.
              zIndex: 1000,
              padding: '0.5rem',
            }}
          >
            {isLoading || loadTimeoutRef.current ? (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                Searching…
              </div>
            ) : options.length > 0 ? (
              options.map((option, index) => {
                const key = getOptionKey(option);
                const isLastSelected = lastSelectedKey === key;
                const isHovered = hoveredIndex === index;
                return (
                  <div
                    key={key}
                    onClick={() => handleOptionClick(option)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      borderRadius: '0.5rem',
                      marginBottom: '0.25rem',
                      // Last-selected wins over hover so the user can
                      // see what they opened even while mousing around.
                      backgroundColor: isLastSelected
                        ? 'rgba(59, 130, 246, 0.18)'
                        : isHovered
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'transparent',
                      // Subtle left bar reinforces "this one is open".
                      borderLeft: isLastSelected
                        ? '3px solid #2563eb'
                        : '3px solid transparent',
                      paddingLeft: isLastSelected ? 'calc(1rem - 3px)' : '1rem',
                      transition: 'background-color 0.15s ease',
                      fontSize: '0.95rem',
                      color: '#000',
                    }}
                  >
                    {itemTemplate(option, isHovered, isLastSelected)}
                  </div>
                );
              })
            ) : hasSearched &&
              !isLoading &&
              !loadTimeoutRef.current &&
              inputValue.length >= minLength &&
              inputValue === lastSearchedTermRef.current ? (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                No results found
              </div>
            ) : inputValue.length < minLength ? (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '0.95rem',
                }}
              >
                Type at least {minLength} characters to search…
              </div>
            ) : (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                Searching…
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default V2AsyncSearchInput;
